import { resolveDestination } from "routing/to";
import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import {
  NavigationDrawerButtonStyles,
  SubrouteLink,
  Accordion,
  AccordionDetails
} from "../NavigationDrawerButton.styles";
import { useAuthState } from "../../../../hooks";
import { NavigationButtonWithSubroutesProps } from "../../../../types";
import { withFeatureVisibility } from "../../../../HoC";

export const NavigationDrawerButtonWithSubroutes = ({
  translationKey,
  subroutes,
  onClick,
  shopId,
  requiredPermission
}: NavigationButtonWithSubroutesProps) => {
  const { pathname } = useLocation();

  const { t, i18n } = useTranslation();

  const { checkPermission } = useAuthState();

  const [subroutesOpened, setSubroutesOpened] = useState<boolean>(false);

  const handleOpenSubroutes = useCallback(() => {
    setSubroutesOpened((state) => !state);
  }, []);

  const handleSubrouteClick = useCallback(() => {
    onClick();
    setSubroutesOpened(false);
  }, [onClick]);

  const parsedSubroutes = useMemo(
    () =>
      subroutes.map(({ translationKey, path, requiredPermission, featureFlag }) => {
        if (requiredPermission && !checkPermission(requiredPermission)) return null;

        const to = resolveDestination(path.path, shopId);
        if (to === undefined) return null;

        if (featureFlag) {
          const Component = withFeatureVisibility(SubrouteLink, featureFlag);

          return (
            <Component key={translationKey} onClick={handleSubrouteClick} to={to}>
              {t(translationKey)}
            </Component>
          );
        }

        return (
          <SubrouteLink key={translationKey} onClick={handleSubrouteClick} to={to}>
            {t(translationKey)}
          </SubrouteLink>
        );
      }),
    [checkPermission, handleSubrouteClick, i18n.language, shopId, subroutes, t]
  );

  const routeActive = subroutes.some(
    ({ path }) => resolveDestination(path.path, shopId) === pathname
  );

  if (
    !parsedSubroutes.some(Boolean) ||
    (requiredPermission && !checkPermission(requiredPermission))
  ) {
    return null;
  }

  return (
    <Accordion expanded={subroutesOpened} onChange={handleOpenSubroutes}>
      <NavigationDrawerButtonStyles
        withSubroutes
        selected={subroutesOpened}
        active={routeActive}
        onClick={handleOpenSubroutes}
      >
        {t(translationKey)}
      </NavigationDrawerButtonStyles>
      <AccordionDetails>{parsedSubroutes}</AccordionDetails>
    </Accordion>
  );
};
